import { env } from '@/utils/env'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
    platformGame?: {
      getFbc?: () => string
      getPixelId?: () => string
    }
  }
}

const FB_SDK_URL = 'https://connect.facebook.net/en_US/fbevents.js'

let sdkLoaded = false
let sdkLoadingPromise: Promise<void> | null = null
let initialized = false
let currentPixelId: string | null = null

function ensureFbqStub() {
  if (typeof window === 'undefined') return
  if (window.fbq) return
  ;(function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return
    n = f.fbq = function () {
      // eslint-disable-next-line prefer-rest-params
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = true
    t.src = v
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window as any, document, 'script', FB_SDK_URL)
}

function loadSdk(): Promise<void> {
  if (sdkLoaded) return Promise.resolve()
  if (sdkLoadingPromise) return sdkLoadingPromise

  ensureFbqStub()
  sdkLoadingPromise = new Promise<void>((resolve) => {
    const onReady = () => {
      sdkLoaded = true
      if (env.isDev) console.log('[FB Pixel] SDK loaded')
      resolve()
    }
    // If the script element is already in the DOM, listen for load; otherwise, rely on stub queueing
    const scripts = Array.from(document.getElementsByTagName('script'))
    const existing = scripts.find((s) => s.src && s.src.indexOf('fbevents.js') !== -1)
    if (existing) {
      if ((existing as any)._fbLoaded) {
        onReady()
      } else {
        existing.addEventListener('load', () => {
          ;(existing as any)._fbLoaded = true
          onReady()
        })
      }
    } else {
      // Stub already injected the script; wait a tick which is enough for stub usage
      setTimeout(onReady, 0)
    }
  })

  return sdkLoadingPromise
}

function resolveFbc(explicit?: string): string | undefined {
  if (explicit) return explicit
  try {
    return window.platformGame?.getFbc?.()
  } catch {
    return undefined
  }
}

export async function initFacebookPixel(pixelId: string, options?: { fbc?: string; autoPageView?: boolean }) {
  if (!pixelId) return false
  await loadSdk()
  const fbc = resolveFbc(options?.fbc)
  try {
    window.fbq('init', pixelId, fbc ? { fbc } : undefined)
    initialized = true
    currentPixelId = pixelId
    if (env.isDev) console.log('[FB Pixel] init', { pixelId, fbc })
    if (options?.autoPageView !== false) trackFacebookPageView()
    return true
  } catch (error) {
    console.error('[FB Pixel] init error', error)
    return false
  }
}

export function isFacebookPixelInitialized() {
  return initialized
}

export function getFacebookPixelId() {
  return currentPixelId
}

export function trackFacebookPageView(params?: Record<string, any>) {
  try {
    ensureFbqStub()
    window.fbq('track', 'PageView', params)
    if (env.isDev) console.log('[FB Pixel] track PageView', params)
  } catch (error) {
    console.error('[FB Pixel] track PageView error', error)
  }
}

export function trackFacebookEvent(eventName: string, params?: Record<string, any>) {
  try {
    ensureFbqStub()
    window.fbq('track', eventName, params)
    if (env.isDev) console.log('[FB Pixel] track', eventName, params)
  } catch (error) {
    console.error('[FB Pixel] track error', eventName, error)
  }
}

export function trackFacebookCustom(eventName: string, params?: Record<string, any>) {
  try {
    ensureFbqStub()
    window.fbq('trackCustom', eventName, params)
    if (env.isDev) console.log('[FB Pixel] trackCustom', eventName, params)
  } catch (error) {
    console.error('[FB Pixel] trackCustom error', eventName, error)
  }
}


